import { toast } from 'sonner';
import i18next from 'i18next';
import { mapColumnFiltersForQuery } from '@/lib/table-filter-query.js';

function mapLegacyPayload(data, sourceType) {
    const title = data.title || data.name;
    const code = String(data.code || '').trim();
    return {
        actionType: 'improvement',
        ...(code ? { code } : {}),
        title,
        description: data.description,
        startDate: data.startDate,
        deadline: data.deadline || data.endDate,
        budget: data.budget,
        progress: data.progress,
        status: mapLegacyStatus(data.status),
        owner: data.owner,
        unit: data.unit,
        sourceType: data.sourceType || sourceType,
        sourceId: data.sourceId,
        sourceTitle: data.sourceTitle,
        improvementObjective: data.improvementObjective || data.description || title,
        assignmentsByRole: data.assignmentsByRole,
    };
}

function mapLegacyStatus(status) {
    const map = {
        'Not Started': 'Pending',
        planned: 'Pending',
        pending: 'Pending',
        'In Progress': 'In Progress',
        'in-progress': 'In Progress',
        Completed: 'Completed',
        completed: 'Completed',
        Delayed: 'Delayed',
        'On Hold': 'Pending',
        Cancelled: 'Pending',
        cancelled: 'Pending',
    };
    return map[status] || status || 'Pending';
}

/**
 * Hub-tab list of unified Actions filtered by actionType=improvement and BCM sourceType.
 */
export class BcmSourcedImprovementActionsIntegration {
    constructor(actionService, { sourceType, store, messagesNs }) {
        this.service = actionService;
        this.sourceType = sourceType;
        this.store = store;
        this.messagesNs = messagesNs;
    }

    _baseFilters() {
        return {
            filter_actionType: ['improvement'],
            filter_sourceType: [this.sourceType],
        };
    }

    async fetchItems(page = 1, pageSize = 10, search = '', sortBy = 'createdAt', sortOrder = 'desc', extra = {}) {
        const store = this.store();
        store.setLoading(true);
        store.clearError?.();
        try {
            const columnFilters = extra.columnFilters
                || (extra.filter_actionType || extra.populate ? {} : extra);
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy: sortBy === 'name' || sortBy === 'code' ? 'title' : sortBy,
                sortOrder,
                ...this._baseFilters(),
                ...mapColumnFiltersForQuery(columnFilters),
            });
            const data = (response.data || []).map((item) => ({
                ...item,
                id: item._id || item.id,
                name: item.title,
            }));
            store.setItems(data);
            store.setUniqueValues?.(response.uniqueValues || {});
            const pagination = response.pagination || response.meta;
            if (pagination) {
                const currentPage = parseInt(pagination.page || pagination.currentPage || 1, 10);
                const totalPages = parseInt(pagination.totalPages || 1, 10);
                store.setPagination({
                    currentPage,
                    itemsPerPage: parseInt(pagination.pageSize || pagination.itemsPerPage || 10, 10),
                    totalItems: parseInt(pagination.total || pagination.totalItems || 0, 10),
                    totalPages,
                    hasNextPage: currentPage < totalPages,
                    hasPreviousPage: currentPage > 1,
                });
            }
            return { success: true, data, uniqueValues: response.uniqueValues || {} };
        } catch (error) {
            const message = error.message || i18next.t('integrationToasts:anErrorOccurred', { defaultValue: 'An error occurred' });
            store.setError?.(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async create(data) {
        return this._mutate(
            () => this.service.create(mapLegacyPayload(data, this.sourceType)),
            i18next.t(`${this.messagesNs}:messages.improvementAction.createSuccess`, { defaultValue: 'Improvement action created' }),
        );
    }

    async update(id, data) {
        return this._mutate(
            () => this.service.update(id, mapLegacyPayload(data, this.sourceType)),
            i18next.t(`${this.messagesNs}:messages.improvementAction.updateSuccess`, { defaultValue: 'Improvement action updated' }),
        );
    }

    async delete(id) {
        return this._mutate(
            () => this.service.delete(id),
            i18next.t(`${this.messagesNs}:messages.improvementAction.deleteSuccess`, { defaultValue: 'Improvement action deleted' }),
        );
    }

    async batchDelete(ids) {
        const store = this.store();
        store.setLoading(true);
        try {
            await this.service.bulkDelete(ids);
            toast.success(i18next.t(`${this.messagesNs}:messages.improvementAction.bulkDeleteSuccess`, { defaultValue: 'Improvement actions deleted' }));
            return { success: true };
        } catch (error) {
            const message = error.message || i18next.t('integrationToasts:anErrorOccurred', { defaultValue: 'An error occurred' });
            toast.error(message);
            store.setError?.(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async locateRowInList(opts) {
        return this.fetchItems(opts?.page || 1, opts?.pageSize || 10, opts?.search || '', opts?.sortBy || 'createdAt', opts?.sortOrder || 'desc');
    }

    async _mutate(operation, successMessage) {
        const store = this.store();
        store.setLoading(true);
        try {
            const response = await operation();
            toast.success(successMessage);
            return { success: true, data: response.data };
        } catch (error) {
            const message = error.message || i18next.t('integrationToasts:anErrorOccurred', { defaultValue: 'An error occurred' });
            toast.error(message);
            store.setError?.(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }
}
